'use client';

import React, { useMemo } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Markdown from '@/components/atoms/project-builder/markdown';
import Button from '@/components/atoms/project-builder/button';
import VariantAttributesList from './variantAttributesList';
import { localeMapping } from '@/helpers/constants/localeMapping';
import { CurrencyHelpers } from '@/helpers/currencyHelpers';
import {
  ITEM_CODE,
  PRODUCT_TYPE,
  RANGE,
  COLOR,
  ITEM_MATERIAL,
  COATING_METHOD,
  ITEM_NAME,
  THUMBNAIL,
  ISCOMPLIANT,
  ISNZCOMPLAINT,
  INDEPENDENT_LIVING_COMPLAINT,
  ISAMBULANT
} from '@/helpers/constants/attributes';
import { typeSelectDataForPublishedProducts } from '@/helpers/server/published-products-select-data';
import { isPrototypeLifecycle } from '@/utils/product-lifecycle';
import { checkIsBuilderProduct } from '@/helpers/builder-product-helper';

const ProductCard = ({ handleTabClick, allowShare, showPricesOnPdf, isLandscapeMode, selectDataForPublishedProducts, ...productDataProps }: any) => {
  const { locale } = useParams();
  const router = useRouter();
  const { roomNotes, variant, quantity, isProductAvailable, slug } = productDataProps;

  const isBuilderProduct = checkIsBuilderProduct({ variant }, selectDataForPublishedProducts, (locale as string) || 'au');
  const localeKey = localeMapping[locale];
  const attributes = variant?.attributes || {};
  const itemAttributes = attributes?.['item-attributes'] || [];
  const assetAttributes = attributes?.['assets-attributes'] || [];
  const productCode = attributes?.[ITEM_CODE];
  const technicalImage = assetAttributes.find((attr: any) => attr.name === 'technical-image')?.value;
  const imageLeft = technicalImage || THUMBNAIL;
  const imageRight = variant?.images?.[0] || THUMBNAIL;
  const selectDataForPublishedProductsLocaleSpecific =
    locale === 'au' ? (selectDataForPublishedProducts as typeSelectDataForPublishedProducts)?.dataAU :
      locale === 'au' ? (selectDataForPublishedProducts as typeSelectDataForPublishedProducts)?.dataAU :
        [];
  const parentProduct = selectDataForPublishedProductsLocaleSpecific?.find((element: any) =>
    element.variants.some((variant: any) => variant.key === productCode)
  );
  const productCategory = parentProduct?.categoryKeys?.[localeKey]?.[0]?.toLowerCase().replace(/\s+/g, '-');
  const productSubCategory = parentProduct?.categoryKeys?.[localeKey]?.[1]?.toLowerCase().replace(/\s+/g, '-');

  const displayAttributes = useMemo(() => {
    const map: Record<string, any> = {};
    itemAttributes.forEach((e: any) => {
      map[e.name] = e.value;
    });
    return map;
  }, [itemAttributes]);

  const isPrototype = isPrototypeLifecycle(
    itemAttributes.find((attr: any) => attr.name === 'lifecycle')?.value
  );

  const addlAttr = useMemo(() => {
    const required = [
      'depth',
      'length',
      'width',
      'net-weight',
      'IsAS14281Compliant',
      'IsAS14281Ambulant',
      'IsIndependentLivingCompliant',
      'IsNZ41121Compliant',
    ];
    return itemAttributes
      .filter((attr: any) => required.includes(attr.name))
      .map((attr: any) => {
        const renameMap: Record<string, string> = {
          IsAS14281Compliant: 'AS1428.1 Accessible',
          IsAS14281Ambulant: 'AS1428.1 Ambulant',
          IsIndependentLivingCompliant: 'Independent Living Compliant',
          IsNZ41121Compliant: 'NZ412.1 Accessible',
        };
        return {
          ...attr,
          name: renameMap[attr.name] || attr.name,
        };
      })
      .filter((attr: any) => {
        if (['AS1428.1 Accessible', 'AS1428.1 Ambulant'].includes(attr.name) && locale === 'au') return true;
        if (attr.name === 'NZ412.1 Accessible' && locale === 'nz') return true;
        return !['AS1428.1 Accessible', 'AS1428.1 Ambulant', 'NZ412.1 Accessible'].includes(attr.name);
      });
  }, [itemAttributes, locale]);

  const navigateProductDetails = () => {
    router.push(`/${slug}/p/${variant.sku}`);
  };

  return (
    <>
      <span className='font-bold print:font-semibold'>{variant.attributes["item-name"]}</span>
      <h4 className="print:text-sm">Code: {productCode}</h4>
      <hr className="hor-rule-hr" />

      <div className="product-images flex flex-col gap-4 print:flex-row print:gap-2 md:flex-row justify-between">
        {imageRight && <img src={imageRight} className="w-full print:w-[40%] md:w-[40%]" alt="Right Image" />}
        {imageLeft && <img src={imageLeft} className="w-full print:w-[40%] md:w-[40%]" alt="Left Image" />}
      </div>

      <div className="hor-rule">
        <h3 className="my-[16px] print:!my-[8px] print:text-sm">Notes</h3>
        <h4 className="print:text-xs">{roomNotes}</h4>
      </div>

      <div className="mb-[5px] grid grid-cols-1 gap-10 print:mb-[5px] print:grid-cols-1 print:gap-10 md:grid-cols-1">
        <div>
          <h3 className="text-lg font-semibold print:text-sm">Attributes</h3>
          <div className="mt-[16px] gap-y-[10px] gap-x-2 md:gap-x-[30px] print:!mt-0 print:!gap-[4px] print:!gap-x-[30px] grid grid-cols-2">

            {/* Standard Attributes */}
            {[ITEM_CODE, PRODUCT_TYPE, RANGE, COLOR, ITEM_MATERIAL, COATING_METHOD].map((attr: string, index: number) => {
              const value = attributes[attr];
              if (
                value &&
                value !== 'Not Applicable' &&
                attr !== ITEM_NAME &&
                !Array.isArray(value)
              ) {
                return (
                  <div key={`std-${index}`} className="flex flex-col print:gap-1 h-full">
                    <div className="flex text-[14px] font-normal leading-6 print:gap-0.5 print:text-xs lg:gap-0 lg:text-16 h-full min-w-0">
                      <span className="flex-1 capitalize text-[#4D4D4D] break-words min-w-0">{attr.replaceAll('-', ' ').replace('gwa', '')}</span>
                      <span className="ml-2 md:ml-[20px] flex-1 text-[#090909] print:ml-0 text-left print:text-left break-words min-w-0">
                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value?.join?.(', ') || value}
                      </span>
                    </div>
                    <div className="attribute-break-h" style={{ marginBottom: '0px' }}></div>

                  </div>
                );
              }
              return null;
            })}

            {/* Variant Attributes */}
            <VariantAttributesList isLandscapeMode={isLandscapeMode} attributes={attributes} />

            {/* Additional Attributes */}
            {addlAttr.map((attr: any, index: number) => (
              <React.Fragment key={index}>
                {(
                  ((attr.name === ISCOMPLIANT || attr.name === ISNZCOMPLAINT || attr.name === INDEPENDENT_LIVING_COMPLAINT) && (
                    ['sanitaryware', 'tapware', 'showers', 'bathroom-accessories', 'basins',
                      'laundry-tubs'].includes(productCategory) ||
                    (productCategory == 'kitchen-sinks-accessories' && productSubCategory == 'kitchen-accessories') ||
                    (productCategory == 'kitchen-sinks-accessories' && ['sinks', 'care-sinks'].includes(productSubCategory))
                  )) ||
                  (attr.name === ISAMBULANT && (
                    ['sanitaryware', 'bathroom-accessories'].includes(productCategory) ||
                    (productCategory == 'kitchen-sinks-accessories' && productSubCategory == 'kitchen-accessories')
                  )) ||
                  ![ISCOMPLIANT, ISNZCOMPLAINT, INDEPENDENT_LIVING_COMPLAINT, ISAMBULANT].includes(attr.name)
                ) && (
                    <div key={`addl-${index}`} className="flex flex-col print:gap-1 h-full">
                      <div className="flex text-[14px] font-normal leading-6 print:gap-0.5 print:text-xs lg:gap-0 lg:text-16 h-full min-w-0">
                        <span className="flex-1 capitalize text-[#4D4D4D] break-words min-w-0">{attr.name}</span>
                        <span className="ml-2 md:ml-[20px] flex-1 text-[#090909] print:ml-0 text-left print:text-left break-words min-w-0">
                          {typeof attr.value === 'boolean' ? (attr.value ? 'Yes' : 'No') : attr.value}
                        </span>
                      </div>
                      <div className="attribute-break-h" style={{ marginBottom: '0px' }}></div>
                    </div>
                  )}
              </React.Fragment>

            ))}
          </div>
        </div>
        <div>

          <h3 className="text-lg font-semibold print:text-sm">Features & Benefits</h3>
          <div className="ml-[35px] list-inside list-disc print:ml-4 print:text-xs [&>ul]:list-disc">
            <Markdown className="text-black [&>ul]:list-disc">
              {displayAttributes.features?.[localeKey]?.replace(/(^|\n)-([^\`])/g, '$1- $2')}
            </Markdown>
          </div>

          {!allowShare && isProductAvailable && (
            <div className="mt-[5px] flex justify-center print:hidden">
              <Button
                variant="secondary"
                className="px-[8px] py-[8px] text-sm font-semibold  cursor-pointer"
                type="button"
                onClick={navigateProductDetails}
                data-html2canvas-ignore
              >
                View Product Detail Page
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="product-price-and-quantity mb-16 print:mb-0">
        <div className="mt-[20px] flex justify-between print:mt-0.5">
          {showPricesOnPdf && !isPrototype && !isBuilderProduct ? (
            <span className="text-2xl text-[#666666] print:text-base mt-[10px]">
              Price:{' '}
              {CurrencyHelpers.formatForCurrency(
                quantity * variant?.price?.centAmount,
                'en',
                'USD',
                2,
              ).replace('US', '')}
            </span>
          ) : (
            <span />
          )}
          <span className="text-[24px] text-[#666666] print:text-base mt-[10px]">Qty {quantity}</span>
        </div>
        <div className="hor-rule-rrp" />
      </div>
    </>
  );
};

export default ProductCard;
